<template>
    <div class="snapshot-chart d-flex p-3" @click="onChartClick">
        <div class="w-50">
            <div class="chart-title mb-4 text-nowrap d-flex">
                <span v-if="titleIcon" class="mr-2">
                    <i v-if="!isSvgIcon(titleIcon)" :class="titleIcon"></i>
                    <span
                        v-else
                        v-html="titleIcon"
                        class="svg-icon-wrapper"
                    ></span>
                </span>
                <h4 class="text-xl">{{ title }}</h4>
            </div>
            <div class="m-auto">
                <h3 class="pb-1 text-nowrap mb-0">{{ value }}</h3>
                <span class="text-nowrap">{{ description }}</span>
            </div>
        </div>
        <Chart
            v-if="chartData.length > 0"
            :chart-id="id"
            :data="chartData"
            :xaxis-labels="xaxisLabels"
            :height="150"
            :ranges="ranges"
            :options="chartOptions"
        />
    </div>
</template>

<script>
import Chart from "@/components/commons/chart.vue";

export default {
    name: "SnapshotChart",
    components: {
        Chart
    },
    props: {
        chartData: {
            type: Array,
            required: true
        },
        xaxisLabels: {
            type: Array,
            required: true
        },
        id: {
            type: String,
            required: true
        },
        title: {
            type: String,
            required: true
        },
        titleIcon: {
            type: String,
            required: false,
            default: ""
        },
        value: {
            type: String,
            required: false
        },
        description: {
            type: String,
            required: false
        },
        unit: {
            type: String,
            required: false
        }
    },
    data() {
        return {
            ranges: []
        };
    },
    computed: {
        chartOptions() {
            const options = {
                series: {
                    lines: {
                        show: true,
                        color: "#000000"
                    },
                    points: {
                        show: false,
                        radius: 3,
                        fillColor: "#000000",
                        color: "#000000"
                    }
                },
                grid: {
                    borderWidth: 1,
                    borderColor: {
                        top: "transparent",
                        right: "transparent",
                        bottom: "#B9B9B9",
                        left: "#B9B9B9"
                    },
                    hoverable: true,
                    margin: {
                        top: 20,
                        right: 50,
                        bottom: 10,
                        left: 20
                    }
                },
                xaxis: {
                    rotateTicks: 0,
                    tickColor: "#B9B9B9"
                },
                yaxis: {
                    tickColor: "transparent",
                    tickSize: 50,
                    tickDecimals: 0
                },
                tooltip: true,
                tooltipOpts: {
                    content: (label, x, y, flotItem) => {
                        let plainLabel = "";
                        if (
                            this.xaxisLabels &&
                            this.xaxisLabels[x] &&
                            typeof this.xaxisLabels[x][1] === "string"
                        ) {
                            let htmlLabel = this.xaxisLabels[x][1];
                            const tempDiv = document.createElement("div");
                            tempDiv.innerHTML = htmlLabel;
                            plainLabel =
                                tempDiv.textContent || tempDiv.innerText || "";
                        }
                        return `${plainLabel}<br/>${y} ${
                            this.unit || ""
                        }`.trim();
                    },
                    shifts: { x: 20, y: 0 },
                    defaultTheme: false
                }
            };

            // If there is only one data point, show the point and hide the line
            // if there is more than one data point, show the line and hide the point
            if (this.chartData && this.chartData.length === 1) {
                options.series.lines.show = false;
                options.series.points.show = true;
            } else {
                options.series.lines.show = true;
                options.series.points.show = false;

                const yValues = this.chartData.map(point => point[1]);

                // Calculate min, max, and middle
                const min = Math.min(...yValues);
                const minValue = min > 0 ? 0 : min;
                const max = Math.max(...yValues);
                const middle = (minValue + max) / 2;

                // show only min, middle, max on y-axis
                options.yaxis.ticks = [minValue, middle, max];
            }

            return options;
        }
    },
    methods: {
        isSvgIcon(iconString) {
            return (
                typeof iconString === "string" &&
                iconString.trim().startsWith("<svg")
            );
        },
        onChartClick() {
            this.$emit("chart-clicked", this.id);
        }
    }
};
</script>

<style scoped>
.snapshot-chart {
    width: 100%;
    background-color: #e8ecff;
    cursor: pointer;
}

.svg-icon-wrapper {
    display: inline-flex; /* Or inline-block */
    align-items: center;
    justify-content: center;
    width: 1.2em;
    height: 1.2em;
    vertical-align: middle;
}

.svg-icon-wrapper :deep(svg) {
    width: 100%;
    height: 100%;
    display: block;
}
</style>
